import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { get, post } from './utils/api';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudiosPage from './pages/StudiosPage';
import PropertiesPage from './pages/PropertiesPage';
import ContractsPage from './pages/ContractsPage';

import TransactionsPage from './pages/TransactionsPage';
import TinkoffImportPage from './pages/TinkoffImportPage';
import PaymentsReportPage from './pages/PaymentsReportPage';
import PLReportPage from './pages/PLReportPage';
import DDSPage from './pages/DDSPage';
import BalancePage from './pages/BalancePage';
import DividendsReportPage from './pages/DividendsReportPage';
import CategoriesPage from './pages/CategoriesPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import './App.css';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const ROLE_LABELS = { admin: 'Администратор', manager: 'Управляющий', fin_manager: 'Финменеджер', cashier: 'Кассир' };

function Layout({ children }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isFinManager = user?.role === 'fin_manager';
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPendingCount = () => {
    get('/import/pending-count').then(d => setPendingCount(d.count || 0)).catch(() => {});
  };

  useEffect(() => {
    if (!isAdmin && !isFinManager && user?.role !== 'manager') return;
    post('/import/tinkoff/auto-fetch').catch(() => {}).finally(refreshPendingCount);
    const handler = () => refreshPendingCount();
    window.addEventListener('pending-import-changed', handler);
    return () => window.removeEventListener('pending-import-changed', handler);
  }, [isAdmin]);

  const nav = isFinManager ? [
    { to: '/transactions', label: 'Все транзакции', icon: '💳' },
    { to: '/import/tinkoff', label: 'Импорт Т-Банк', icon: '🏦' },
    { to: '/reports/pl', label: 'P&L', icon: '📊' },
    { to: '/reports/dds', label: 'ДДС', icon: '💰' },
    { to: '/reports/balance', label: 'Баланс', icon: '⚖️' },
    { to: '/reports/dividends', label: 'Дивиденды', icon: '🎯' },
  ] : [
    { to: '/', label: 'Дашборд', icon: '⬜' },
    { to: '/contracts', label: 'Договоры с арендаторами', icon: '👥' },
    { to: '/studios', label: 'Студии', icon: '🏠' },
    { to: '/reports/payments', label: 'Оплаты арендаторов', icon: '✅' },
    null,
    { to: '/transactions', label: 'Все транзакции', icon: '💳' },
    { to: '/import/tinkoff', label: 'Импорт Т-Банк', icon: '🏦' },
    { to: '/reports/pl', label: 'P&L', icon: '📊' },
    { to: '/reports/dds', label: 'ДДС', icon: '💰' },
    { to: '/reports/balance', label: 'Баланс', icon: '⚖️' },
    { to: '/reports/dividends', label: 'Дивиденды', icon: '🎯' },
    null,
    { to: '/properties', label: 'Помещения', icon: '🏢' },
    { to: '/categories', label: 'Справочники', icon: '🗂️' },
    { to: '/users', label: 'Пользователи', icon: '👤', adminOnly: true },
    { to: '/settings', label: 'Настройки', icon: '⚙️', adminOnly: true },
  ];

  return (
    <div className="layout">
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <span className="logo-icon">🏢</span>
          <span className="logo-text">РентМенеджер</span>
        </div>
        <nav className="sidebar-nav">
          {nav.map((n, i) => {
            if (n === null) return <div key={i} className="nav-divider" />;
            if (n.adminOnly && !isAdmin) return null;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}>
                <span className="nav-icon">{n.icon}</span>
                <span>{n.label}</span>
                {n.to === '/import/tinkoff' && pendingCount > 0 && (
                  <span className="nav-badge">{pendingCount}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-user">
          <div className="user-name">{user?.name}</div>
          <div className="user-role-badge" data-role={user?.role}>{ROLE_LABELS[user?.role] || user?.role}</div>
          <button className="logout-btn" onClick={logout}>Выйти</button>
        </div>
      </aside>
      <div className="main-wrap">
        <header className="topbar">
          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>☰</button>
          <div className="topbar-title">РентМенеджер</div>
          <div className="topbar-user">{user?.name}</div>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    return t && u ? JSON.parse(u) : null;
  });

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };
  const logout = () => {
    post('/logout').catch(() => {}).finally(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
          {user ? (
            <Route path="/*" element={<Layout><Routes>
              <Route path="/" element={user?.role === 'fin_manager' ? <Navigate to="/import/tinkoff" /> : <DashboardPage />} />
              <Route path="/studios" element={<StudiosPage />} />
              <Route path="/properties" element={<PropertiesPage />} />
              <Route path="/contracts" element={<ContractsPage />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/import/tinkoff" element={<TinkoffImportPage />} />
              <Route path="/reports/payments" element={<PaymentsReportPage />} />
              <Route path="/reports/pl" element={<PLReportPage />} />
              <Route path="/reports/dds" element={<DDSPage />} />
              <Route path="/reports/balance" element={<BalancePage />} />
              <Route path="/reports/dividends" element={<DividendsReportPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes></Layout>} />
          ) : (
            <Route path="*" element={<Navigate to="/login" />} />
          )}
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
